import React, { useContext, useState } from "react";
import cx from "classnames";
import styles from "../scss/DapQuestion.module.scss";
import Icon from "../../common/components/Icon/Icon";
import Dropdown from "../../common/components/Forms/Dropdown/Dropdown";
import { IDropdownOption } from "../../common/interfaces/IDropdownOption";
import Button from "../../common/components/Forms/Button/Button";
import { AdviceControllerContext } from "../providers/DigitalAdviceProvider";
import {
    IDigitalSubQuestion,
    IQuestionOptionImage
} from "../interfaces/IDigitalAdviceData";
import { createRange } from "../shared/digitalAdviceHelper";
import DAQuestionFieldImage from "./DAQuestionFieldImage";
import { ISumbitParams } from "./DigitalAdviceQuestion";
import { Form, Formik } from "formik";

interface IManageDependentChildProps {
    subQuestionList: IDigitalSubQuestion[];
    onSubmit: (answerList: ISumbitParams[]) => void;
    addMultipleFrameImages?: (images: IQuestionOptionImage[], isAdded: boolean) => void;
    setImagePlaceholders?: (images: IQuestionOptionImage[]) => void;
    resetFramePlaceholder?: () => void;
}

interface IChildrenType {
    id: number;
    age: string;
    imgUrl: string;
}

const ManageDependentChild: React.FC<IManageDependentChildProps> = (props) => {
    const { subQuestionList, onSubmit } = props;

    const [children, setChildren] = useState<IChildrenType[]>([]);
    const [imgPlaceholder, setImgPlaceholder] = useState([]);
    const [disabledBtn, setDisabledBtn] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const { selectors } = useContext(AdviceControllerContext);

    const getMaxDependentChild = (): number => {
        return (subQuestionList && subQuestionList.length) || 0;
    };
    const [maxDependentChild, setMaxDependentChild] = useState(getMaxDependentChild());

    const getDependentChild = (): IDigitalSubQuestion => {
        return subQuestionList && subQuestionList.length && subQuestionList[0];
    };
    const [DependentChild, setDependentChild] = useState(getDependentChild());

    const getAgeOptions = (start: number, end: number): IDropdownOption[] => {
        return createRange(start, end).map((i) => ({
            value: i.toString(),
            label: i.toString()
        }));
    };
    const ageOptions = [{ value: null, label: null }, ...getAgeOptions(0, 18)];

    const handleAddButtonDisabled = (list): boolean => {
        if (list && list.length === 0) return false;
        return !!list.find((i) => i.age === null)?.id || false;
    };

    const handleImagesPlaceholder = (subQuestionId, age): Array<IQuestionOptionImage> => {
        const { questionOptionImage } = subQuestionList.find(
            (i) => i.id === subQuestionId
        );
        const parseAge = parseInt(age);

        const optionImage = questionOptionImage.find(
            (item) =>
                parseAge >= item.responseValueMin && parseAge <= item.responseValueMax
        );
        const filterPlaceholder = imgPlaceholder.filter((i) => i.id !== subQuestionId);
        const newList = [
            ...filterPlaceholder,
            {
                id: subQuestionId,
                questionOptionImage: optionImage
            }
        ];
        return newList;
    };

    const handleSetPlaceholder = (placeholders): void => {
        props.setImagePlaceholders(
            placeholders.reduce((acc, i) => {
                return [...acc, { ...i.questionOptionImage }];
            }, [])
        );
    };

    const handleAgeChanges = (child, value): void => {
        if (!value) {
            return;
        }

        setIsSubmit(false);
        const imagePlaceholder = handleImagesPlaceholder(child.id, value);
        handleSetPlaceholder(imagePlaceholder);
        setImgPlaceholder(imagePlaceholder);
        const newList = children.map((obj) => {
            if (obj.id === child.id) {
                return { ...obj, age: value };
            }
            return obj;
        });
        setDisabledBtn(handleAddButtonDisabled(newList));
        setChildren(newList);
    };

    const addEmptyChild = (): void => {
        ``;
        setIsSubmit(false);
        const lastChildIndex = children.length;
        const child = {
            id: subQuestionList[lastChildIndex].id,
            imgUrl: subQuestionList[lastChildIndex].imgUrl,
            age: null
        };
        const imagePlaceholder = handleImagesPlaceholder(child.id, child?.age || "0");
        handleSetPlaceholder(imagePlaceholder);
        setImgPlaceholder(imagePlaceholder);
        const newList = [...children, child];
        setDisabledBtn(handleAddButtonDisabled(newList));
        setChildren(newList);
    };

    const removeChildOnList = (childId): void => {
        setIsSubmit(false);
        const lastChild = children[children.length - 1];
        let filterList = [];
        let filterPlaceholder = [];
        if (lastChild.id === childId) {
            filterList = children.filter((i) => i.id !== childId);
            filterPlaceholder = imgPlaceholder.filter((i) => i.id !== childId);
        } else {
            const updatedList = children.map((i) => {
                if (i.id === childId) {
                    i.age = lastChild.age;
                }
                return i;
            });
            filterList = updatedList.filter((i) => i.id !== lastChild.id);
            filterPlaceholder = handleImagesPlaceholder(childId, lastChild.age).filter(
                (i) => i.id !== lastChild.id
            );
        }
        handleSetPlaceholder(filterPlaceholder);
        setImgPlaceholder(filterPlaceholder);
        setDisabledBtn(handleAddButtonDisabled(filterList));
        setChildren(filterList);
    };

    const handleOnSubmit = (): void => {
        if (isSubmit) {
            return;
        }
        setIsSubmit(true);
        const questionOptionImage = imgPlaceholder.reduce((acc, item) => {
            return [...acc, item.questionOptionImage];
        }, []);
        props.addMultipleFrameImages(questionOptionImage, true);
        props.resetFramePlaceholder();
        const answerList = children.reduce((acc, item) => {
            const obj = {
                questionId: item.id,
                textValue: item.age,
                sessionUserId: selectors.getSessionUserId()
            };
            acc = [...acc, obj];
            return acc;
        }, []);
        onSubmit(answerList);
    };

    return (
        <Formik initialValues={{}} onSubmit={handleOnSubmit}>
            {() => (
                <Form
                    className={styles["field-dependentchild"]}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleOnSubmit();
                        }
                    }}>
                    <div className={styles["child-dependent"]}>
                        {children.map((child, idx) => (
                            <div className={styles["create-child"]} key={child.id}>
                                <div className={styles["img-container"]}>
                                    <img src={child.imgUrl} />
                                </div>
                                <span>Child {idx + 1}'s age</span>
                                <div className={styles["dependent-field"]}>
                                    <Dropdown
                                        id={`dropdown-${child.id}`}
                                        name={`dropdown-${child.id}`}
                                        options={ageOptions}
                                        selectedOptionId={child.age}
                                        onChange={(e) => {
                                            handleAgeChanges(child, e.target.value);
                                        }}
                                        variant={"default"}
                                        className={styles.dropdownStyle}
                                    />
                                </div>
                                <div className={styles.deleteIcon}>
                                    <button onClick={() => removeChildOnList(child.id)}>
                                        <Icon id="TrashCan" size="xl" />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {children.length < maxDependentChild && (
                            <button
                                className={styles["create-child-button"]}
                                disabled={disabledBtn}
                                onClick={addEmptyChild}>
                                <div className={styles["img-container"]}>
                                    <DAQuestionFieldImage
                                        imgUrl={DependentChild.imgUrl}
                                    />
                                </div>
                                <span>
                                    {children.length
                                        ? "Add another child"
                                        : "Add a child"}
                                </span>
                                <Icon
                                    className={styles.icon}
                                    id="AccordionPlus"
                                    size="l"
                                />
                            </button>
                        )}
                    </div>

                    <div className={styles.buttonNext}>
                        <Button
                            className={cx("get-started")}
                            width="content"
                            component="button"
                            size="m"
                            type="submit"
                            isDisabled={isSubmit || disabledBtn}
                            variant="calltoaction">
                            <span>{children.length ? "Next" : "Skip"}</span>
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default ManageDependentChild;
